export const SUPPORTED_LOCALES = [
  'en',
  'zh-CN',
  'zh-TW',
  'ru',
  'ja',
  'th',
  'vi',
  'id',
  'es',
  'hi',
  'fil',
  'fr',
  'de',
  'pt',
  'tr',
  'ko',
  'ar',
  'ur',
] as const;

export type Locale = (typeof SUPPORTED_LOCALES)[number];
export type Direction = 'ltr' | 'rtl';

export interface LocaleMeta {
  code: Locale;
  nativeName: string;
  englishName: string;
  direction: Direction;
}

export const LOCALE_META: Readonly<Record<Locale, LocaleMeta>> = Object.freeze({
  en: { code: 'en', nativeName: 'English', englishName: 'English', direction: 'ltr' },
  'zh-CN': { code: 'zh-CN', nativeName: '简体中文', englishName: 'Simplified Chinese', direction: 'ltr' },
  'zh-TW': { code: 'zh-TW', nativeName: '繁體中文', englishName: 'Traditional Chinese', direction: 'ltr' },
  ru: { code: 'ru', nativeName: 'Русский', englishName: 'Russian', direction: 'ltr' },
  ja: { code: 'ja', nativeName: '日本語', englishName: 'Japanese', direction: 'ltr' },
  th: { code: 'th', nativeName: 'ภาษาไทย', englishName: 'Thai', direction: 'ltr' },
  vi: { code: 'vi', nativeName: 'Tiếng Việt', englishName: 'Vietnamese', direction: 'ltr' },
  id: { code: 'id', nativeName: 'Bahasa Indonesia', englishName: 'Indonesian', direction: 'ltr' },
  es: { code: 'es', nativeName: 'Español', englishName: 'Spanish', direction: 'ltr' },
  hi: { code: 'hi', nativeName: 'हिन्दी', englishName: 'Hindi', direction: 'ltr' },
  fil: { code: 'fil', nativeName: 'Filipino', englishName: 'Filipino', direction: 'ltr' },
  fr: { code: 'fr', nativeName: 'Français', englishName: 'French', direction: 'ltr' },
  de: { code: 'de', nativeName: 'Deutsch', englishName: 'German', direction: 'ltr' },
  pt: { code: 'pt', nativeName: 'Português', englishName: 'Portuguese', direction: 'ltr' },
  tr: { code: 'tr', nativeName: 'Türkçe', englishName: 'Turkish', direction: 'ltr' },
  ko: { code: 'ko', nativeName: '한국어', englishName: 'Korean', direction: 'ltr' },
  ar: { code: 'ar', nativeName: 'العربية', englishName: 'Arabic', direction: 'rtl' },
  ur: { code: 'ur', nativeName: 'اردو', englishName: 'Urdu', direction: 'rtl' },
});

export function isLocale(value: string): value is Locale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

export function normalizeLocale(value: string | null | undefined): Locale {
  if (!value) return 'en';
  const normalized = value.trim().replace('_', '-').toLowerCase();
  if (normalized === 'zh-tw' || normalized === 'zh-hk' || normalized === 'zh-hant' || normalized.startsWith('zh-hant-')) return 'zh-TW';
  if (normalized === 'zh-cn' || normalized === 'zh-hans' || normalized.startsWith('zh-hans-')) return 'zh-CN';
  if (normalized === 'fil' || normalized === 'tl' || normalized.startsWith('fil-') || normalized.startsWith('tl-')) return 'fil';
  if (normalized.startsWith('pt')) return 'pt';
  if (normalized.startsWith('es')) return 'es';
  if (normalized.startsWith('hi')) return 'hi';
  if (normalized.startsWith('ur')) return 'ur';
  const base = normalized.split('-')[0]!;
  return isLocale(base) ? base : 'en';
}

export { messages, catalogResources, type MessageKey } from './messages';
export { localeForCountry } from './region';
